/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Row, Col } from "react-awesome-styled-grid";
import { useForm, FieldErrors, FieldError } from "react-hook-form";
import { Button, Sidebar, theme } from "@nordcloud/gnui";
import { KeyValueInput } from "~/generated/graphql";
import { showError } from "~/services/toast";
import { isNotEmpty } from "~/tools";
import { InputName, InputValue } from "./Inputs";
import { tagSchema } from "./validators";

type FormData = {
  name: string;
  value: string;
};

const ColumnCss = {
  marginBottom: theme.spacing.spacing04,
};

type Props = {
  onSubmit: (data: KeyValueInput) => void;
  onClose: () => void;
  isOpen: boolean;
};

export function AddTagForm({ onSubmit, onClose, isOpen }: Props) {
  const { register, setValue, handleSubmit, watch } = useForm<FormData>({
    resolver: yupResolver(tagSchema),
    defaultValues: {
      name: "",
      value: "",
    },
  });

  const onUpdate = (data: FormData) => {
    onSubmit({
      key: data.name,
      value: data.value,
    });
  };

  const onError = (error: FieldErrors) => {
    Object.values(error).forEach((err: FieldError) =>
      err.message ? showError(err.message) : undefined
    );
  };

  React.useEffect(() => {
    register("name", { required: true });
    register("value", { required: true });
  }, [register]);

  const watcher = watch();

  const hasValues = Object.values(watcher).every((value) => isNotEmpty(value));
  const disabled = !hasValues;

  return (
    <Sidebar title="Add Tag" isOpen={isOpen} onClick={onClose}>
      <form data-testid="tag-form" onSubmit={handleSubmit(onUpdate, onError)}>
        <Row>
          <Col css={ColumnCss} xs={12}>
            <InputName onChange={(value) => setValue("name", value)} />
          </Col>
          <Col css={ColumnCss} xs={12}>
            <InputValue
              name={watcher.name}
              onChange={(value) => setValue("value", value)}
            />
          </Col>
        </Row>
        <Button disabled={disabled} type="submit">
          Add Tag
        </Button>
      </form>
    </Sidebar>
  );
}
